/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { Dimensions, TextInput, View } from "react-native";
import { NavLink } from "react-router-dom";
import * as Slack from "node-slack";

import { useDispatch, useSelector } from "react-redux";
import { RootState, VideoInfoType } from "store/types";

import {
  ArchiveVideo,
  Button,
  LoadingView,
  Text,
  styles,
  ProprietaryPopupModal,
} from "UpduoLibrary";
import {
  skaleMainBlue,
  skalePrimaryBlue,
  skaleWhite,
  skaleWebBorderGray,
  skaleWebFillLightGray,
} from "config/UpduoColors";
import { GET_STAFF_SESSION_SECRET } from "graphql/internal/Queries";
import { GET_SESSION_SECRET } from "graphql/public/Queries";
import { ActionCreators } from "actions";
import { GetSessionSecretQuery } from "graphql/public/generated_public";
import { GetStaffSessionSecretQuery } from "graphql/internal/generated";

const windowHeight = Dimensions.get("window").height;

export default function VideoRoute({ match }) {
  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [allowView, setAllowView] = useState(false);

  const [
    getStaffSessionSecret,
    {
      data: getStaffSessionSecretData,
      loading: getStaffSessionSecretLoading,
      error: getStaffSessionSecretError,
    },
  ] = useLazyQuery<GetStaffSessionSecretQuery>(GET_STAFF_SESSION_SECRET, {
    onCompleted: ({ staffSession }) => {
      if (staffSession?.organization?.showProprietaryPopup) {
        dispatch(ActionCreators.setProprietaryPopupModalIsVisible(true));
      }
    },
  });

  const [
    getSessionSecret,
    {
      data: getSessionSecretData,
      loading: getSessionSecretLoading,
      error: getSessionSecretError,
    },
  ] = useLazyQuery<GetSessionSecretQuery>(GET_SESSION_SECRET);

  const videoInfo =
    getStaffSessionSecretData?.staffSession || getSessionSecretData?.session;

  useEffect(() => {
    if (
      match.params.idHash !== "example" &&
      !getStaffSessionSecretLoading &&
      !getSessionSecretLoading &&
      !getSessionSecretError &&
      !getStaffSessionSecretError
    ) {
      if (authToken) {
        getStaffSessionSecret({
          variables: {
            getStaffSessionSecretRequest: {
              sessionId: match.params.idHash,
            },
          },
        });
      } else {
        getSessionSecret({
          context: { isPublic: true },
          variables: {
            getSessionSecretRequest: {
              sessionId: match.params.idHash,
            },
          },
        });
      }
    }
  }, [
    authToken,
    getStaffSessionSecretLoading,
    getSessionSecretLoading,
    match.params.idHash,
  ]);

  const _handleEmailChangeText = (email: string) => {
    setEmail(email);
    const regexPattern = /.+@.+\..+/g;
    setIsEmailValid(regexPattern.test(email));
  };

  const _handleKeyPress = (e) => {
    if (e.key === "Enter") {
      _handleButtonPress();
    }
  };

  const _handleButtonPress = useCallback(() => {
    if (isEmailValid) {
      const urlWebHook =
        "https://hooks.slack.com/services/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";
      const payload = {
        username: "Upduo Videos",
        text: `New video access!\n${window.location.href}\n${email}`,
      };
      const slack = new Slack(urlWebHook, {});
      slack.send(payload);
      setAllowView(true);
    }
  }, [isEmailValid, email]);

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
      }}
    >
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: windowHeight - 40,
          backgroundColor: skaleWebFillLightGray,
          zIndex: -10,
        }}
      />
      <View
        style={{
          marginTop: 50,
          alignSelf: "center",
          backgroundColor: skaleWhite,
          borderWidth: 1,
          borderColor: skaleWebBorderGray,
        }}
      >
        <View
          style={{
            width: 1066,
            height: 753,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {videoInfo ? (
            authToken || allowView ? (
              <ArchiveVideo
                videoInfo={videoInfo as NonNullable<VideoInfoType>}
                standalone={true}
                url={window.location.href}
              />
            ) : (
              <>
                <Text style={styles.textInputHeader}>
                  {"Please enter your email to view video:"}
                </Text>
                <TextInput
                  value={email}
                  onChangeText={(email) => _handleEmailChangeText(email)}
                  autoCapitalize={"none"}
                  autoCorrect={false}
                  style={styles.textInput}
                  autoFocus={true}
                  onKeyPress={_handleKeyPress}
                />
                <View style={{ height: 20 }} />
                <Button
                  onPress={() => {
                    _handleButtonPress();
                  }}
                  text={"View Video"}
                  disabled={!isEmailValid}
                  height={36}
                  borderWidth={1}
                  borderColor={skalePrimaryBlue}
                  color={skaleWhite}
                  textColor={skalePrimaryBlue}
                />
              </>
            )
          ) : (
            <>
              <View style={{ flex: 1 }} />
              {getSessionSecretLoading || getStaffSessionSecretLoading ? (
                <LoadingView color={skaleMainBlue} topSpacing={100} />
              ) : (
                <View style={{ alignItems: "center" }}>
                  <Text
                    style={{
                      textAlign: "center",
                      fontWeight: "bold",
                      marginBottom: 10,
                    }}
                  >
                    {"This video doesn't exist, or can't be accessed."}
                  </Text>
                  <NavLink
                    to={authToken ? "/join_organization" : "/start"}
                    // activeStyle={{ color: activeColor }}
                    style={{ textDecoration: "none" }}
                  >
                    {authToken
                      ? "You may need to join a community."
                      : "You must log in to view this video."}
                  </NavLink>
                </View>
              )}
              <View style={{ flex: 1 }} />
            </>
          )}
        </View>
      </View>
      <ProprietaryPopupModal origin={"video_route"} />
    </View>
  );
}
